import {
  AfterViewInit, ChangeDetectorRef, Component, ComponentFactoryResolver, ElementRef, OnDestroy, OnInit, TemplateRef,
  ViewChild, ViewContainerRef
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CarsService } from '../cars.service';
import { faHeart as fasFaHeart, faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import { faHeart as farFaHeart } from '@fortawesome/free-regular-svg-icons';
import { CarDialogOverviewComponent } from '../car-dialog-overview/car-dialog-overview.component';
import { CarModalComponent } from '../car-modal/car-modal.component';
import {NbDialogConfig, NbDialogRef, NbDialogService} from '@nebular/theme';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CalculatorCarsComponent } from '../../calculator/calculator-cars.component';
import { BehaviorSubject, forkJoin } from 'rxjs';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { AuthenticateService } from '../../auth/authenticate.service';
import { ChangeContext, LabelType, Options } from '@angular-slider/ngx-slider';
import { CurrencyPipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../services/language.service';
import { CurrencyService } from '../../services/currency.service';
import { LocaleService } from '../../services/locale.service';
import { switchMap } from 'rxjs/operators';
import { trigger, style, transition, animate, query, stagger } from '@angular/animations';
import {CookieService} from 'ngx-cookie-service';
import {SubscriptionDialogComponent} from '../../payment/subscription-dialog.component';
import {ThankYouDialogComponent} from '../car-modal/thank-you-dialog.component';

export interface Model {
  name: string;
  fuelType: string;
  fuelType_h: string;
}

export interface FilterMeta {
  year: number;
  make: string;
  make_h: string;
  model: Model[];
}

export interface DialogData {
  car: any;
}

declare let dataLayer: Array<any>;

@Component({
  selector: 'app-cars-grid',
  templateUrl: './cars-grid.component.html',
  styleUrls: ['./cars-grid.component.scss'],
  animations: [
    trigger('fadeAnimation', [
      transition('* <=> *', [
        query(':enter', [
          style({ opacity: 0 }),
          stagger('100ms', animate('300ms ease-in', style({ opacity: 1 })))
        ], { optional: true }),
        query(':leave', [
          animate('300ms ease-out', style({ opacity: 0 }))
        ], { optional: true })
      ])
    ])
  ]
})
export class CarsGridComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('carContainer') carContainer: ElementRef;
  @ViewChild('loadingOverlay') loadingOverlay: TemplateRef<any>;

  currencyPipe = new CurrencyPipe('he-il');


  overlayRef: OverlayRef;

  loading = false;
  hasMore = true;

  page = 1;

  shouldHandleQueryParams = true;

  // price range
  sliderValue = 0;
  sliderHighValue = 5000000;
  sliderOptions: Options = {
    floor: 0,
    ceil: 5000000,
    step: 400000,
    rightToLeft: true,
    animate: true,
    pushRange: true,
    animateOnMove: true,
    showSelectionBar: true,
    hideLimitLabels: false,
    autoHideLimitLabels: false,
    ticksTooltip: (value: number): string => {
      return '';
    },
    selectionBarGradient: {
      from: '#00ff00',
      to: '#ff0000'
    },
    translate: (value: number, label: LabelType): string => {
      return '';
    }

  };

  // km range
  sliderKmValue = 0;
  sliderKmHighValue = 1000000;
  sliderKmOptions: Options = {
    floor: 0,
    ceil: 1000000,
    step: 10000,
    rightToLeft: true,
    animate: true,
    pushRange: true,
    animateOnMove: true,
    showSelectionBar: true,
    hideLimitLabels: false,
    autoHideLimitLabels: false,
    ticksTooltip: (value: number): string => {
      return '';
    },
    selectionBarGradient: {
      from: '#00ff00',
      to: '#ff0000'
    },
    translate: (value: number, label: LabelType): string => {
      return '';
    }

  };

  locatedInIsrael: 'All' | 'Yes' | 'No' = 'All';
  saleCondition: 'All' | 'New' | 'Used' = 'All';
  importType: 'Commercial' | 'Taxi' = 'Commercial';
  year = -1;
  make = '';
  model = '';
  fueltype = '';
  priceRange = '0-5000000';
  kmRange = '0-1000000';

  tabSelectionEnabled = false;


  constructor(
    private activatedRoute: ActivatedRoute,
    private carsService: CarsService,
    public dialogService: NbDialogService,
    private breakpointObserver: BreakpointObserver,
    public dialog: MatDialog,
    private cd: ChangeDetectorRef,
    private componentFactoryResolver: ComponentFactoryResolver,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private authService: AuthenticateService,
    private router: Router,
    private cookieService: CookieService,
    private translate: TranslateService,
    private languageService: LanguageService,
    public currencyService: CurrencyService,
    public localeService: LocaleService,
    // public nbDialogRef: NbDialogRef<SubscriptionDialogComponent>,
  ) {
  }

  private isEnglish(): boolean {
    return this.languageService.getCurrentLanguage() === 'en';
  }

  // Display-only translation for filter options. The bound [value] stays the
  // Hebrew string (make_h / fuel label / 'הכל') so all existing filter and
  // search logic is byte-for-byte unchanged; only the visible text changes.
  displayMake(makeH: string): string {
    if (makeH === 'הכל') { return this.translate.instant('COMMON.ALL'); }
    if (!this.isEnglish()) { return makeH; }
    const m: any = (this.filterMetaMakes as any[]).find((x: any) => x.make_h === makeH);
    return m && m.make ? m.make : makeH;
  }

  displayFuelType(label: string): string {
    if (label === 'הכל') { return this.translate.instant('COMMON.ALL'); }
    if (!this.isEnglish()) { return label; }
    const f: any = (this.filterMetaFuelTypes as any[]).find((x: any) => x && x.label === label);
    return f && f.name ? f.name.charAt(0).toUpperCase() + f.name.slice(1) : label;
  }

  displayYear(year: any): string {
    if (year === 'הכל') { return this.translate.instant('COMMON.ALL'); }
    return year;
  }

  // Model names come from the backend in English only; just translate the
  // 'All' sentinel and pass everything else through untouched.
  displayModel(model: string): string {
    if (model === 'הכל') { return this.translate.instant('COMMON.ALL'); }
    return model;
  }

  @ViewChild('host', { read: ViewContainerRef }) host: ViewContainerRef;
  isLoading = new BehaviorSubject<boolean>(false);
  vehicleValue = new BehaviorSubject<any>(null);

  fasFaHeart = fasFaHeart;
  farFaHeart = farFaHeart;
  faShoppingCart = faShoppingCart;

  cars = [];
  fetchedCars = [];
  currencies: any;
  carById: any; // fetched by link id, since we work by random and filter

  filterMetaCars: FilterMeta[] = [];
  filterMetaYears: any[] = [];
  filterMetaMakes = [];
  filterMetaFuelTypes = [];
  filterMetaModels = [];

  years = ['הכל'];
  makes = ['הכל'];
  models = ['הכל'];
  fuelTypes = ['הכל'];

  selectedYear = 'הכל';
  selectedMake = 'הכל';
  selectedModel = 'הכל';
  selectedFuelType = 'הכל';

  isFilterCollapsed = false;

  showDirham = false;
  carModalDialogLoaded = false;


  ngOnInit() {
    if (!this.localeService.israeliMarket) { this.selectedTab = 0; }
    this.currencyService.ensureLoaded();
    // /cars (commercial import) inventory is Israel-located, so a 'No' default
    // would hide almost all of it. Keep 'All' here (the Israel filter is still
    // hidden for non-ILS via the template); only the marketplace defaults to 'No'.
    this.locatedInIsrael = 'All';

    setTimeout(() => {
      // Non-ILS users only have the Marketplace tab (Collectibles is hidden), so
      // keep selectedTab at 0 — a stale 'selectedTab' cookie of 1 would otherwise
      // short-circuit handleQueryParams() and break the "More Details" dialog.
      this.selectedTab = this.localeService.israeliMarket
        ? parseInt(this.cookieService.get('selectedTab') || '0', 0)
        : 0;
    }, 1000);

    this.showDirham = sessionStorage.getItem('user-id') === '530';
    this.isFilterCollapsed = this.isMobileView();
  }

  ngOnDestroy() {
  }

  castUSDToDirham(usd: number): number {
    const conversionRate = 3.67; // Example conversion rate, please use the current rate
    return usd * conversionRate;
  }

  // Method to convert EUR to Dirham
  castEURToDirham(eur: number): number {
    const conversionRate = 4.05; // Example conversion rate, please use the current rate
    return eur * conversionRate;
  }

  getCarPrice(car: any) {
    if (this.showDirham) {
      return (this.castUSDToDirham(car.CarPrice) + this.castEURToDirham(2000)) * 1.05 + this.castUSDToDirham(1500);
    }
    return car.C_Price;
  }

  // onScroll(event: any): void {
  //   const element = event.target;
  //   console.log(`scrollTop: ${element.scrollTop}, scrollHeight: ${element.scrollHeight}, offsetHeight: ${element.offsetHeight}, clientHeight: ${element.clientHeight}`);
  //   if (element.scrollHeight - element.scrollTop <= element.clientHeight + 1) {
  //     console.log('load  0 -------');
  //     // Reached the bottom of the scrollable area
  //     if (!this.isLoading) {
  //       console.log('load -------');
  //       // this.loadMoreItems();
  //     }
  //   }
  // }

  target;

  isAndroid(): boolean {
    // Check if the user agent is Android
    return /android/i.test(navigator.userAgent);
  }

  resetScrollToTop(): void {
    // setTimeout(() => {
    // if (document.documentElement.scrollTop || document.body.scrollTop) {
    //   window.scroll(0, 0);  // For older browsers
    // } else {
    //   window.scrollTo({ top: 0, behavior: 'auto' }); // For modern browsers
    // }}, 100);

    // setTimeout(() => {
    if (this.target) {
      this.target.scrollTo({ top: 0, behavior: 'smooth' });
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
    // }}, 200);

  }

  resetScroll = () => {
    if (this.isAndroid()) {
      // this.target?.removeEventListener('scroll', this.onWindowScroll, true);
      this.resetScrollToTop();
      // this.target?.addEventListener('scroll', this.onWindowScroll, true);
    } else {
      // this.target?.removeEventListener('scroll', this.onWindowScroll, true);
      this.target?.scrollTo({ top: 0, behavior: 'smooth' });
      // this.target?.addEventListener('scroll', this.onWindowScroll, true);
    }
  }

  onWindowScroll = (event: any): void => {

    if (this.carModalDialogLoaded) {
      return;
    }
    // console.log(event);
    // const element = document.getElementById('cardContainer');
    const element = event.target;
    if (this.isAndroid()) {
      this.target = event.target;
    } else {
      this.target = element;
    }
    if (element) {
      const pos = element.scrollTop + element.offsetHeight;
      const max = element.scrollHeight;

      if (pos >= max) {
        console.log('load ---------');
        // Your logic to load more items or whatever you want to do when reaching the bottom


        const params = {
          page: this.page + 1,
          year: this.year,
          make: this.make,
          model: this.model,
          fueltype: this.fueltype,
          priceRange: this.priceRange,
          kmRange: this.kmRange,
          locatedInIsrael: this.locatedInIsrael,
          saleCondition: this.saleCondition,
          importType: this.importType,
        };

        // console.log(pos, max);


        this.loadMoreItems(params);

      }
    }
  }

  handleLoadMore = (): void => {
    if (this.carModalDialogLoaded) {
      return;
    }


    console.log('load ---------');
    // Your logic to load more items or whatever you want to do when reaching the bottom


    const params = {
      page: this.page + 1,
      year: this.year,
      make: this.make,
      model: this.model,
      fueltype: this.fueltype,
      priceRange: this.priceRange,
      kmRange: this.kmRange,
      locatedInIsrael: this.locatedInIsrael,
      saleCondition: this.saleCondition,
      importType: this.importType,
    };


    this.loadMoreItems(params);

  }

  async initialize() {
    const data = await this.checkUserAuthentication();
    console.log(data);

    // if (data.cpms && !data.allowCM) {
    //   // show dialog
    //   const dialogConfig = {
    //     hasBackdrop: true,
    //     closeOnEsc: true,
    //     backdropClass: '',
    //     closeOnBackdropClick: false,
    //     hasScroll: false,
    //     autoFocus: false,
    //     viewContainerRef: undefined,
    //   };
    //
    //   const dialogRef = this.dialogService.open(SubscriptionDialogComponent, dialogConfig);
    //
    //   dialogRef.onClose.subscribe((result: any) => {
    //     // this.nbDialogRef.close(data);
    //   });
    //   // const dialogRef = this.dialog.open(SubscriptionDialogComponent);
    //
    //   // after 2 seconds, close the dialog and navigate to /subscribe
    //   setTimeout(() => {
    //     dialogRef.close();
    //     this.router.navigate(['/subscribe']);
    //   }, 2000);
    // } else {
      // await this.checkUserAuthentication();
    this.createOverlay();
    this.loadData();

    // }
  }

  async checkUserAuthentication() {
    const userId = sessionStorage.getItem('user-id');
    // if (!userId) {
    const res: any = await this.authService.checkAuth().toPromise();
    if (res.code === 200) {
      sessionStorage.setItem('user-id', res.data.id);
      // Your dataLayer logic
      return res.data;
    }
    // }
    return null;
  }

  get isLoggedIn() {
    return sessionStorage.getItem('user-id') !== null;
  }

  // async checkUserAuthentication() {
  //   const userId = sessionStorage.getItem('user-id');
  //   if (!userId) {
  //     this.authService.checkAuth().subscribe((res: any) => {
  //       if (res.code === 200) {
  //         sessionStorage.setItem('user-id', res.data.id);
  //         // Your dataLayer logic
  //       }
  //     });
  //   }
  // }

  createOverlay() {
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'loading-backdrop',
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.carContainer)
        .withPositions([
          {
            originX: 'center',
            originY: 'center',
            overlayX: 'center',
            overlayY: 'center',
          },
        ]),
    });

    const loadingPortal = new TemplatePortal(this.loadingOverlay, this.viewContainerRef);
    this.overlayRef.attach(loadingPortal);
  }

  loadData() {
    forkJoin([
      this.carsService.filterPaged({
        page: 1,
        year: this.year,
        make: this.make,
        model: this.model,
        fueltype: this.fueltype,
        priceRange: this.priceRange,
        kmRange: this.kmRange,
        locatedInIsrael: this.locatedInIsrael,
        saleCondition: this.saleCondition,
        importType: this.importType,
      }),
      // this.calculatorService.getCurrencies(),
      this.carsService.getFilterMeta(),
    ]).subscribe(([cars, filterMeta]) => {
      this.cars = cars.map((car) => ({
        ...car,
        isLiked: car.interest === 'Liked'
      }));
      this.fetchedCars = this.cars;

      // this.currencies = currencies.data;
      // console.log(filterMeta);
      this.filterMetaCars = filterMeta.cars;

      this.filterMetaModels = [].concat(...this.filterMetaCars.map(vehicle => vehicle.model));

      this.filterMetaFuelTypes = ['הכל'].concat(filterMeta.fueltypes);
      this.fuelTypes = ['הכל'].concat(filterMeta.fueltypes.map((fuelType) => fuelType.label));
      this.filterMetaYears = ['הכל'].concat(filterMeta.years);


      this.filterMetaMakes = filterMeta.manufacturers;
      this.makes = ['הכל'].concat(filterMeta.manufacturers.map((manufacturer) => manufacturer.make_h));
      // console.log(this.cars);
      // console.log(new Date().getTime());

      this.overlayRef.detach();

      this.handleQueryParams();

      this.hasMore = cars.length !== 0;
    });
  }

  handleQueryParams() {
    if (this.selectedTab != 0) {
      return;
    }
    this.activatedRoute.queryParams
      .pipe(
        switchMap(async (params) => {
          const id = params['id'];
          this.importType = this.localeService.israeliMarket ? (params['importType'] ?? 'Commercial') : 'Commercial';
          this.selectedTab = params['tab'] ?? 0;
          setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
            this.cd.markForCheck();
          }, 0);

          if (id) {
            return await this.getCarDataById(id);
          }
          const dialogResult = this.carsService.carResponseSubject.value;
          if (dialogResult) {
            dataLayer.push({
              event: 'cars-grid-dialog-closed_coll_pub',
              item_id: dialogResult.id,
              item_name: dialogResult.Manufacturer + ' ' + dialogResult.Model,
              item_price: dialogResult.CarPrice,
              item_year: dialogResult.ManufactureDate,
            });
          }
          return null;
        })
      )
      .subscribe(
        (carData) => {
          if (carData) {
            this.toggleMoreInfo(carData);
          }
        },
        (error) => {
          console.error(`An error occurred: ${error}`);
        }
      );
  }


  loadMoreItems(params: any) {
    if (this.selectedTab != 0) {
      return;
    }
    this.carsService.filterPaged(params).subscribe(cars => {
      this.cars = [...this.cars, ...cars.map((car) => ({
        ...car,
        isLiked: car.interest === 'Liked'
      }))
      ];
      this.fetchedCars = this.cars;
      this.page++;

      console.log('page', this.page);
      console.log(this.cars);
      this.hasMore = cars.length !== 0;
    });
  }

  async getCarDataById(id: string): Promise<any> {
    let carById = this.cars.find((car) => car.id === +id);
    if (!carById) {
      const cars = await this.carsService.getCarById(+id).toPromise();
      carById = cars[0];
    }
    return carById;
  }

  ngAfterViewInit() {
    this.tabSelectionEnabled = true;
    this.initialize();
    this.isLoading.next(true);
    const factory = this.componentFactoryResolver.resolveComponentFactory(CalculatorCarsComponent);
    this.isLoading.next(false);
  }

  openDialog(car: any): void {
    this.dialog.open(CarDialogOverviewComponent, {
      width: '80vw',
      data: { car }
    });
  }

  toggleLike(car) {
    car.isLiked = !car.isLiked;
    const interest = car.isLiked ? 'Liked' : 'Disliked';
    this.carsService.updateInterest(car.id, interest, this.importType).subscribe((res: any) => {
      console.log('bb', res);
    });
  }

  toggleInterest(car) {
    this.carsService.updateInterest(car.id, 'Interested', this.importType).subscribe((res: any) => {
      console.log('aa', res);
    });
  }

  isMobileView(): boolean {
    return this.breakpointObserver.isMatched(Breakpoints.Handset);
  }

  async handleClick(car: any) {
    await this.router.navigate([], {
      queryParams: {
        id: car.id,
        importType: this.importType,
        tab: 0
      },

      queryParamsHandling: 'merge'
    });
  }
  toggleMoreInfo(car: any) {

    this.shouldHandleQueryParams = false;

    dataLayer.push({
      event: 'button_click_more_info_list',
      item_id: car.id,
      item_name: car.Manufacturer + ' ' + car.Model,
      item_price: car.CarPrice,
      item_year: car.ManufactureDate,
      user_id: sessionStorage.getItem('user-id'),
      userId: sessionStorage.getItem('user-id'),
    });

    const isMobile = this.isMobileView();

    const dialogConfig: NbDialogConfig<any> = {
      hasBackdrop: true,
      closeOnEsc: true,
      backdropClass: '',
      dialogClass: isMobile ? 'full-screen-dialog' : '',
      closeOnBackdropClick: false,
      hasScroll: false,
      autoFocus: false,
      viewContainerRef: undefined,
      context: {
        isCarMarket: false,
        data: car ?? {},
        currencies: this.currencies,
        isLoadingCalc: this.isLoading,
        vehicleValue: this.vehicleValue,
        importType: this.importType,
        coll: false,
        tab: 0
      }
    };

    this.carsService.updateInterest(car.id, 'RequestedMoreInfo', this.importType).subscribe((res: any) => {
      console.log('cc', res);
    });

    this.carModalDialogLoaded = true;

    if (!isMobile) {
      const dialogRef = this.dialogService.open(CarModalComponent, dialogConfig);

      dialogRef.onClose.subscribe((result: any) => {
        try {
          this.carModalDialogLoaded = false;
          // setTimeout(() => {
          this.router.navigate([], {
            queryParams: {
              id: null,
              importType: this.importType,
              tab: parseInt(this.cookieService.get('selectedTab') || '0', 0)
            },
            queryParamsHandling: 'merge'
          });
          // });

          // Action to be performed when the dialog closes
          const dialogResult = result ?? dialogConfig.context.data;
          console.log('Dialog closed', dialogResult);

          dataLayer.push({
            event: 'cars-grid-dialog-closed',
            item_id: dialogResult.id,
            item_name: dialogResult.Manufacturer + ' ' + dialogResult.Model,
            item_price: dialogResult.CarPrice,
            item_year: dialogResult.ManufactureDate,
          });


        } catch (err: any) {
          console.error(err);
        }
        this.shouldHandleQueryParams = true;
      });

    } else {

      const dialogRef = this.dialogService.open(CarModalComponent, dialogConfig);
      dialogRef.componentRef.instance.nbDialogRef = dialogRef; // Set the dialogRef in the component

      dialogRef.onClose.subscribe((result: any) => {
        try {
          this.carModalDialogLoaded = false;
          // setTimeout(() => {
          this.router.navigate([], {
            queryParams: {
              id: null,
              importType: this.importType,
              tab: 0
            },
            queryParamsHandling: 'merge'
          });
          // });

          // Action to be performed when the dialog closes
          const dialogResult = result ?? dialogConfig.context.data;
          console.log('Dialog closed', dialogResult);

          dataLayer.push({
            event: 'cars-grid-dialog-closed',
            item_id: dialogResult.id,
            item_name: dialogResult.Manufacturer + ' ' + dialogResult.Model,
            item_price: dialogResult.CarPrice,
            item_year: dialogResult.ManufactureDate,
          });


        } catch (err: any) {
          console.error(err);
        }
        this.shouldHandleQueryParams = true;
      });

    }
  }

  getExwByExwId(data: any, id) {
    for (const option of data.options) {
      for (const exw of option.exw) {
        if (exw.id === id) {
          return exw;
        }
      }
    }

    return null;
  }

  getHarborByExwId(data, id) {
    return data.options.find(option =>
      option.exw.some(exw => exw.id === id)
    );
  }

  getDisHarborByExwId = (data, id) => {
    for (const option of data.options) {
      for (const exw of option.exw) {
        if (exw.id === id) {
          return exw.dHarbor;
        }
      }
    }

    return null;
  };

  onYearChange(year: string) {
    this.selectedYear = year;
  }

  onMakeChange(make: string) {
    this.selectedMake = make;

    this.models = Array.from(new Set(
      ['הכל'].concat(
        this.filterMetaCars
          .filter((car) => car.make_h === make)
          .map((car) => car.model.map((model: any) => model.name))
          .reduce((acc, val) => acc.concat(val), [])
      )
    ));

    this.selectedModel = 'הכל';
  }

  onModelChange(model: string) {
    this.selectedModel = model;
  }

  onFuelChange(fuel: string) {
    this.selectedFuelType = fuel;
  }

  toggleFilter() {
    this.isFilterCollapsed = !this.isFilterCollapsed;
  }

  // type = normal | interested | liked
  filter(type: string = 'normal') {
    this.loading = true;
    // Show overlay
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'loading-backdrop',
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.carContainer)
        .withPositions([{
          originX: 'center',
          originY: 'center',
          overlayX: 'center',
          overlayY: 'center'
        }])
    });


    const loadingPortal = new TemplatePortal(this.loadingOverlay, this.viewContainerRef);

    // const loadingPortal = new ComponentPortal(this.loadingOverlay);
    this.overlayRef.attach(loadingPortal);

    if (type === 'normal') {
      const year = this.selectedYear === 'הכל' ? -1 : parseInt(this.selectedYear, 0);
      const make = this.selectedMake === 'הכל' ? '' : this.filterMetaMakes.find((m: any) => m.make_h === this.selectedMake)?.make ?? '';

      // TODO: fix this
      const model = this.selectedModel === 'הכל' ? '' : this.selectedModel;

      // TODO: fix this
      let fueltype = '';
      switch (this.selectedFuelType) {
        case 'הכל':
          fueltype = '';
          break;
        case 'דיזל':
          fueltype = 'diesel';
          break;
        case 'בנזין':
          fueltype = 'petrol';
          break;
        case 'היברידי':
          fueltype = 'hybrid';
          break;
        case 'חשמלי':
          fueltype = 'electric';
          break;
        case 'פלאג אין':
          fueltype = 'plug-in';
          break;

      }
      // const fueltype = this.selectedFuelType === 'הכל'
      //     ? ''
      //     : this.filterMetaModels.find((m: any) => m?.fuelType_h === this.selectedFuelType)?.fuelType ?? '';

      this.year = year,
        this.make = make,
        this.model = model,
        this.fueltype = fueltype,
        this.priceRange = `${this.sliderValue}-${this.sliderHighValue}`,
        this.kmRange = `${this.sliderKmValue}-${this.sliderKmHighValue}`,

        this.carsService.filterPaged({
          page: 1,
          year: this.year,
          make: this.make,
          model: this.model,
          fueltype: this.fueltype,
          priceRange: `${this.sliderValue}-${this.sliderHighValue}`,
          kmRange: `${this.sliderKmValue}-${this.sliderKmHighValue}`,
          locatedInIsrael: this.locatedInIsrael,
          saleCondition: this.saleCondition,
          importType: this.importType
        })
          .subscribe((cars: any) => {
            this.cars = cars.map((car) => ({
              ...car,
              isLiked: car.interest === 'Liked'
            }));
            this.fetchedCars = this.cars;
            this.filterByRange();
            this.loading = false;
            this.toggleFilter();
            this.overlayRef.detach();
            console.log('reset scroll call');
            this.resetScroll();
            this.hasMore = cars.length !== 0;
          });
    } else if (type === 'interested') {
      this.carsService.filterInterested({})
        .subscribe((cars: any) => {
          this.cars = cars.map((car) => ({
            ...car,
            isLiked: car.interest === 'Liked' || car.interest === 'RequestedMoreInfo'
          }));
          this.fetchedCars = this.cars;
          this.filterByRange();
          this.loading = false;
          this.toggleFilter();
          this.overlayRef.detach();
          this.resetScroll();
        });

    } else if (type === 'liked') {
      this.carsService.filterLiked({})
        .subscribe((cars: any) => {
          this.cars = cars.map((car) => ({
            ...car,
            isLiked: true, //car.interest === 'Liked'
          }));
          this.fetchedCars = this.cars;
          this.filterByRange();
          this.loading = false;
          this.toggleFilter();
          this.overlayRef.detach();
          this.resetScroll();
        });
    }
  }

  onSliderChangeEnd($event: ChangeContext) {
    console.log($event);
    this.filterByRange();
    console.log(this.cars.length);
  }

  onSliderKmChangeEnd($event: ChangeContext) {
    console.log($event);
    this.filterByRange();
    console.log(this.cars.length);
  }

  filterByRange() {
    this.cars = this.fetchedCars;
    console.log(this.cars);
    this.cars = this.cars.filter((car) => {
      return (car.C_Price >= this.sliderValue && car.C_Price <= this.sliderHighValue) &&
        (car.Mileage >= this.sliderKmValue && car.Mileage <= this.sliderKmHighValue) &&
        ((car.Location === 'Israel' && this.locatedInIsrael === 'Yes')
          || (car.Location !== 'Israel' && this.locatedInIsrael === 'No')
          || this.locatedInIsrael === 'All') &&
        ((car.SaleCondition === 'New' && this.saleCondition === 'New')
          || (car.SaleCondition !== 'New' && this.saleCondition === 'Used')
          || this.saleCondition === 'All');
    });
  }

  onLocationToggleChange($event: any): void {
    this.filterByRange();
  }

  onCarSaleConditionToggleChange($event: any): void {
    this.filterByRange();
  }

  onCarImportTypeToggleChange($event: any): void {
    this.router.navigate([], {
      queryParams: {
        id: null,
        importType: this.importType,
        tab: 0
      },
      queryParamsHandling: 'merge'
    });
    this.filterByRange();
  }


  getFuelTypeHebrew(type: any) {
    if (this.showDirham) { return type; }

    switch (type) {
      case '':
        return '';
      case 'diesel':
        return this.translate.instant('FUEL_TYPE.DIESEL');
      case 'petrol':
        return this.translate.instant('FUEL_TYPE.PETROL');
      case 'hybrid':
        return this.translate.instant('FUEL_TYPE.HYBRID');
      case 'electric':
        return this.translate.instant('FUEL_TYPE.ELECTRIC');
      case 'plug-in':
        return this.translate.instant('FUEL_TYPE.PLUG_IN');

    }
  }

  selectedTab = 0;
  onTabIndexChanged($event: number) {


    if (!this.tabSelectionEnabled) {
      return;
    } else {
      this.selectedTab = $event;
      this.selectedTab = (+$event || 0);
      this.cookieService.set('selectedTab', $event.toString());
    }
  }
}



